/**
 * RasterSamplingService — Real DEM/COG sampling for accurate volumes and profiles
 *
 * Supports sampling elevation from Cloud Optimized GeoTIFF (COG) layers at arbitrary points.
 * Used by Survey/Design tools for cut/fill and profile generation from real terrain.
 * Sampling failures are explicit: this service never substitutes synthetic elevations.
 */

import type { Feature, MultiPolygon, Polygon } from 'geojson';

export interface SamplePoint {
  easting: number;
  northing: number;
  elevation: number;
}

export class RasterSamplingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RasterSamplingError';
  }
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function pointInRing(point: [number, number], ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const intersects =
      yi > point[1] !== yj > point[1] && point[0] < ((xj - xi) * (point[1] - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

function pointInPolygon(point: [number, number], polygon: number[][][]): boolean {
  return (
    pointInRing(point, polygon[0]) && !polygon.slice(1).some((hole) => pointInRing(point, hole))
  );
}

export class RasterSamplingService {
  private cogCache = new Map<string, any>(); // URL -> GeoTIFF instance

  /**
   * Sample elevations at points from a COG raster layer.
   * Rejects invalid layers, inaccessible rasters, out-of-bounds points, and invalid/nodata cells.
   */
  async sampleFromCOG(
    rasterLayer: { sourceUrl: string; sourceType?: string },
    points: Array<{ easting: number; northing: number }>
  ): Promise<SamplePoint[]> {
    if (!points || points.length === 0) return [];
    if (!rasterLayer?.sourceUrl || rasterLayer.sourceType !== 'cog') {
      throw new RasterSamplingError(
        'A valid COG raster source is required for elevation sampling.'
      );
    }

    try {
      const { fromUrl } = await import('geotiff');
      let tiff = this.cogCache.get(rasterLayer.sourceUrl);
      if (!tiff) {
        tiff = await fromUrl(rasterLayer.sourceUrl);
        this.cogCache.set(rasterLayer.sourceUrl, tiff);
      }

      const image = await tiff.getImage(0);
      const bbox = image.getBoundingBox() as [number, number, number, number];
      const width = image.getWidth();
      const height = image.getHeight();
      if (
        width <= 0 ||
        height <= 0 ||
        bbox.some((value) => !Number.isFinite(value)) ||
        bbox[2] === bbox[0] ||
        bbox[3] === bbox[1]
      ) {
        throw new RasterSamplingError('The COG has invalid dimensions or bounds.');
      }

      const rasters = await image.readRasters();
      const data = Array.isArray(rasters) ? rasters[0] : rasters;
      if (!data) {
        throw new RasterSamplingError('The COG does not contain a readable elevation band.');
      }

      const rawNoData = image.getGDALNoData?.();
      const noData = rawNoData === null || rawNoData === undefined ? null : Number(rawNoData);

      return points.map((point, index) => {
        if (!Number.isFinite(point.easting) || !Number.isFinite(point.northing)) {
          throw new RasterSamplingError(`Sample point ${index + 1} has invalid coordinates.`);
        }

        // COG and point coordinates are expected to use the same CRS. This assumes a north-up image.
        const px = Math.floor(((point.easting - bbox[0]) / (bbox[2] - bbox[0])) * width);
        const py = Math.floor(((bbox[3] - point.northing) / (bbox[3] - bbox[1])) * height);
        if (px < 0 || px >= width || py < 0 || py >= height) {
          throw new RasterSamplingError(`Sample point ${index + 1} is outside the COG bounds.`);
        }

        const rawElevation = (data as ArrayLike<number>)[py * width + px];
        const elevation = Number(rawElevation);
        if (
          rawElevation === null ||
          rawElevation === undefined ||
          !Number.isFinite(elevation) ||
          (noData !== null && Number.isFinite(noData) && elevation === noData)
        ) {
          throw new RasterSamplingError(
            `No valid elevation is available at sample point ${index + 1}.`
          );
        }

        return {
          easting: point.easting,
          northing: point.northing,
          elevation: Number(elevation.toFixed(2)),
        };
      });
    } catch (error) {
      if (error instanceof RasterSamplingError) throw error;
      throw new RasterSamplingError(`COG elevation sampling failed: ${errorMessage(error)}`);
    }
  }

  /** Sample profile elevations along an alignment using a COG DEM. */
  async sampleProfile(
    rasterLayer: unknown,
    alignmentStations: Array<{ station: number; easting: number; northing: number }>
  ): Promise<Array<{ station: number; elevation: number }>> {
    const points = alignmentStations.map((station) => ({
      easting: station.easting,
      northing: station.northing,
    }));
    const sampled = await this.sampleFromCOG(
      rasterLayer as { sourceUrl: string; sourceType?: string },
      points
    );
    return sampled.map((sample, index) => ({
      station: alignmentStations[index].station,
      elevation: sample.elevation,
    }));
  }

  /**
   * Compute cut/fill volume against a constant design elevation.
   * A DEM alone is not a design surface, so a missing design elevation is rejected explicitly.
   */
  async computeAccurateVolume(
    boundaryFeature: Feature,
    designElevation: number | null,
    demLayer: unknown,
    areaSqm: number
  ): Promise<{ cut: number; fill: number; net: number; avgElev: number }> {
    if (designElevation === null || !Number.isFinite(designElevation)) {
      throw new RasterSamplingError(
        'Cut/fill is unavailable without a finite design elevation or design surface.'
      );
    }
    if (!Number.isFinite(areaSqm) || areaSqm <= 0) {
      throw new RasterSamplingError(
        'Cut/fill is unavailable because the boundary area is invalid.'
      );
    }

    const geometry = boundaryFeature.geometry;
    let polygons: number[][][][];
    if (geometry.type === 'Polygon') {
      polygons = [(geometry as Polygon).coordinates];
    } else if (geometry.type === 'MultiPolygon') {
      polygons = (geometry as MultiPolygon).coordinates;
    } else {
      throw new RasterSamplingError('Cut/fill requires a Polygon or MultiPolygon boundary.');
    }

    const allCoordinates = polygons.flat(2);
    if (allCoordinates.length < 3) {
      throw new RasterSamplingError('Cut/fill requires a valid polygon boundary.');
    }

    const minX = Math.min(...allCoordinates.map((coordinate) => coordinate[0]));
    const maxX = Math.max(...allCoordinates.map((coordinate) => coordinate[0]));
    const minY = Math.min(...allCoordinates.map((coordinate) => coordinate[1]));
    const maxY = Math.max(...allCoordinates.map((coordinate) => coordinate[1]));
    if (![minX, maxX, minY, maxY].every(Number.isFinite) || minX === maxX || minY === maxY) {
      throw new RasterSamplingError('Cut/fill requires a valid polygon extent.');
    }

    // Use a deterministic interior grid so samples never include a polygon's exterior or holes.
    const candidates: Array<{ easting: number; northing: number }> = [];
    const gridSize = 32;
    for (let row = 0; row < gridSize; row++) {
      for (let column = 0; column < gridSize; column++) {
        const x = minX + ((column + 0.5) / gridSize) * (maxX - minX);
        const y = minY + ((row + 0.5) / gridSize) * (maxY - minY);
        if (polygons.some((polygon) => pointInPolygon([x, y], polygon))) {
          candidates.push({ easting: x, northing: y });
        }
      }
    }
    if (candidates.length === 0) {
      throw new RasterSamplingError('No valid interior DEM sample locations were found.');
    }

    const targetSampleCount = Math.min(50, candidates.length);
    const testPoints = Array.from(
      { length: targetSampleCount },
      (_, index) => candidates[Math.floor((index * candidates.length) / targetSampleCount)]
    );
    const sampled = await this.sampleFromCOG(
      demLayer as { sourceUrl: string; sourceType?: string },
      testPoints
    );
    if (sampled.length !== testPoints.length || sampled.length === 0) {
      throw new RasterSamplingError('The DEM did not return all required elevation samples.');
    }

    let sumElev = 0;
    let cutDepth = 0;
    let fillDepth = 0;
    sampled.forEach((sample) => {
      const elevation = sample.elevation;
      sumElev += elevation;
      const difference = designElevation - elevation;
      if (difference > 0) fillDepth += difference;
      else cutDepth += -difference;
    });

    const scale = areaSqm / sampled.length;
    const totalCut = cutDepth * scale;
    const totalFill = fillDepth * scale;
    const avgElev = sumElev / sampled.length;

    return {
      cut: Math.round(totalCut),
      fill: Math.round(totalFill),
      net: Math.round(totalCut - totalFill),
      avgElev: Number(avgElev.toFixed(2)),
    };
  }
}

export const rasterSamplingService = new RasterSamplingService();
